// La barra por celda del lienzo de un diseño (spec §3.3) — la que el lienzo monta por su
// ranura `Barra` en vez de `BarraDeCelda`. Toda celda con imagen tiene los mismos tres
// botones (ADR-2026-09-17-todas-las-imagenes-tienen-las-mismas-acciones), y antes de
// ellos Ver y Agregar al chat a la vista, con su texto (pedido de Matías del 2026-09-18:
// en el "⋯" no se encontraban); el "⋯" queda para Descargar y Quitar. Mientras hay una
// mezcla armándose, las PARTES no tienen barra: tocarlas es sumarlas o sacarlas de la
// mezcla, y lo que se les pide es la cajita "¿qué tomar?" del adorno, no Ver/Agregar ni
// arrancar otra mezcla desde ahí (pedido de Matías del 2026-09-21).

import { useContext, useEffect, useRef, useState } from 'react';
import { Blend, Download, Eye, Loader2, MessageSquarePlus, MoreHorizontal, PenTool, PencilRuler, Shirt, Trash2, Users } from 'lucide-react';
import { useExitTransition } from '../../../../hooks/useExitTransition';
import { BOTON_DE_BARRA, CAJA_DE_BARRA, ITEM_DE_MENU, MS_MENU } from '../../../../chat-atlas/lienzo/BarraDeCelda';
import { adjuntoDeCelda } from '../../../../chat-atlas/lienzo/accionesDeCelda';
import { LienzoContext, type PropsDeBarraDeCelda } from '../../../../chat-atlas/lienzo/lienzoContext';
import { descargarCreacion } from '../../components/descargarCreacion';
import { descargarDesdeBlob } from '../../../../utils/descargarArchivo';
import { vectorizarDeDiseno } from './generarEnDiseno';
import { useDiseno } from './disenoContext';
import { CANTIDAD_POR_ACCION } from './promptsDeDiseno';
import { ACCIONES_CON_BOTON, type AccionConBoton } from './accionesDeDiseno';
import { RESULTADO_DE_MEZCLA_DEFAULT } from './disenoGuardado';

const ICONO: Record<AccionConBoton, typeof Shirt> = { 'en-uso': Users, mezcla: Blend, plano: PencilRuler };

export const BarraDeDiseno: React.FC<PropsDeBarraDeCelda> = ({ id, celda, animada }) => {
  const lienzo = useContext(LienzoContext);
  const d = useDiseno();
  const [menu, setMenu] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [vectorizando, setVectorizando] = useState(false);
  const { mounted: menuMontado, animated: menuAbierto } = useExitTransition(menu, MS_MENU);
  const raizRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!animada) setMenu(false);
  }, [animada]);
  useEffect(() => {
    if (!menu) return;
    const alTocar = (e: PointerEvent) => {
      if (raizRef.current && !raizRef.current.contains(e.target as Node)) setMenu(false);
    };
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(false);
    };
    document.addEventListener('pointerdown', alTocar, true);
    document.addEventListener('keydown', alTeclado);
    return () => {
      document.removeEventListener('pointerdown', alTocar, true);
      document.removeEventListener('keydown', alTeclado);
    };
  }, [menu]);

  const accionar = (accion: AccionConBoton) => {
    setMenu(false);
    if (accion === 'mezcla') {
      // Arranca de cero cada vez (ADR-2026-09-17-la-mezcla-no-se-guarda).
      d.setMezcla(() => ({ baseId: id, partes: [], instruccion: '', cantidad: CANTIDAD_POR_ACCION.mezcla, resultado: RESULTADO_DE_MEZCLA_DEFAULT }));
      d.abrirPanel({ accion: 'mezcla', celdaId: id });
      return;
    }
    d.abrirPanel({ accion, celdaId: id });
  };
  const agregarAlChat = () => {
    setMenu(false);
    if (celda) {
      const chip = adjuntoDeCelda(celda);
      if (chip) lienzo.agregarAdjunto(chip);
      return;
    }
    const chip = d.adjuntoDePropia(id);
    if (chip) lienzo.agregarAdjunto(chip);
  };
  const descargar = async () => {
    setMenu(false);
    setDescargando(true);
    try {
      if (celda) {
        await descargarCreacion({ companyId: celda.companyId, datos: celda.datos, esVideo: false, resolverOpciones: lienzo.resolverOpciones, aplicarOpciones: lienzo.aplicarOpciones });
      } else {
        const url = d.urlDe(id);
        if (!url) throw new Error('No se pudo leer la imagen.');
        const blob = await (await fetch(url)).blob();
        // El helper de la casa: un <a> a mano que revoca el object URL en el mismo tick
        // cancela la descarga en algunos navegadores, y este además cubre iOS.
        const extension = blob.type === 'image/jpeg' ? '.jpg' : '.png';
        await descargarDesdeBlob({ blob, fileName: `${d.tituloDe(id) || 'imagen'}${extension}` });
      }
    } catch (e) {
      d.avisarError(e instanceof Error ? e.message : 'No se pudo descargar.');
    } finally {
      setDescargando(false);
    }
  };

  const esParteDeLaMezcla = !!d.mezcla && d.mezcla.partes.some((p) => p.celdaId === id);
  if (esParteDeLaMezcla) return null;

  /** De dónde sale el archivo original de esta celda: una creación vive en `generadas`
   *  bajo `full/`; una imagen propia, en el bucket de adjuntos con su path guardado.
   *  Una creación sin `fileName` (un 3,5% de las filas: videos y tandas que fallaron a
   *  medias) no tiene archivo que vectorizar — el mismo guard que `accionesDeCelda`. */
  const origenDeStorage = (): { bucket: string; path: string } | null => {
    if (celda) {
      return celda.datos.fileName
        ? { bucket: 'generadas', path: `${celda.companyId}/full/${celda.datos.fileName}` }
        : null;
    }
    const propia = d.diseno.imagenes.find((i) => i.id === id);
    return propia ? { bucket: propia.bucket, path: propia.path } : null;
  };
  // Sin archivo de origen, "Descargar SVG" sólo podría avisar que no se pudo: no se
  // ofrece (hallazgo de la revisión del PR #128).
  const origenSvg = origenDeStorage();
  const descargarSvg = async (origen: { bucket: string; path: string }) => {
    setMenu(false);
    setVectorizando(true);
    try {
      const svg = await vectorizarDeDiseno(origen);
      await descargarDesdeBlob({
        blob: new Blob([svg], { type: 'image/svg+xml' }),
        fileName: `${d.tituloDe(id) || 'plano'}.svg`,
      });
    } catch (e) {
      d.avisarError(e instanceof Error ? e.message : 'No se pudo convertir a SVG.');
    } finally {
      setVectorizando(false);
    }
  };

  return (
    <div
      ref={raizRef}
      className={`nodrag nopan relative origin-bottom transition-[opacity,transform] duration-200 ease-out ${animada ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-1.5 scale-95 opacity-0'}`}
    >
      <div className={`flex items-center gap-0.5 rounded-full ${CAJA_DE_BARRA}`}>
        {celda && (
          <button type="button" onClick={() => lienzo.abrir(celda.imageId)} className={BOTON_DE_BARRA} title="Ver en el visor">
            <Eye className="h-4 w-4" aria-hidden="true" />
            Ver
          </button>
        )}
        <button type="button" onClick={agregarAlChat} className={BOTON_DE_BARRA} title="Agregar al chat">
          <MessageSquarePlus className="h-4 w-4" aria-hidden="true" />
          Agregar al chat
        </button>
        <span className="mx-0.5 h-5 w-px shrink-0 bg-[#02062B]/[0.08]" aria-hidden="true" />
        {ACCIONES_CON_BOTON.map(({ accion, rotulo }) => {
          const Icono = ICONO[accion];
          return (
            <button key={accion} type="button" onClick={() => accionar(accion)} className={BOTON_DE_BARRA} title={rotulo}>
              <Icono className="h-4 w-4" aria-hidden="true" />
              {rotulo}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setMenu((v) => !v)}
          aria-expanded={menu}
          aria-haspopup="menu"
          aria-label="Más"
          className={`${BOTON_DE_BARRA} w-9 justify-center px-0 ${menu ? 'bg-[#02062B]/[0.06]' : ''}`}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {menuMontado && (
        <div role="menu" className={`absolute right-0 top-full mt-2 flex min-w-[196px] origin-top-right flex-col gap-0.5 rounded-2xl transition-[opacity,transform] duration-150 ease-out ${CAJA_DE_BARRA} ${menuAbierto ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
          <button type="button" role="menuitem" onClick={() => void descargar()} disabled={descargando} className={ITEM_DE_MENU}>
            {descargando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
            Descargar
          </button>
          {/* Vectorizar cuesta plata (Recraft) y sólo rinde con un dibujo de línea, así que
              es un ítem aparte del "⋯" y nunca la descarga por defecto. */}
          {origenSvg && (
            <button type="button" role="menuitem" onClick={() => void descargarSvg(origenSvg)} disabled={vectorizando} className={ITEM_DE_MENU}>
              {vectorizando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <PenTool className="h-4 w-4" aria-hidden="true" />}
              Descargar SVG
            </button>
          )}
          {/* Cualquier celda: se esconde del lienzo y queda en la biblioteca del diseño
              (ADR-2026-09-17-agregar-imagen-es-la-biblioteca-del-diseno). */}
          <button type="button" role="menuitem" onClick={() => { setMenu(false); d.quitarDelLienzo(id); }} className={`${ITEM_DE_MENU} text-red-600`}>
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Quitar del lienzo
          </button>
        </div>
      )}
    </div>
  );
};
